import TradingPlatformBadge from './components/TradingPlatformBadge';
import {displayApi,invalidateOperatorSummary,type FollowerDisplay} from '@/lib/forex-display-api';
import {ConnectionCell} from './components/AccountDisplayCells';
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi } from "@/lib/forex-api";

type UiStatus = "pending" | "provisioning" | "connected" | "rejected" | "need_more" | "unknown";
type ActionType = "approve" | "reject" | "need_more";

interface SubAccountRow {
  facts: FollowerDisplay;
  id: string;
  followerAccountId: string;
  userRef: string;
  broker: string;
  server: string;
  accountNumber: string;
  appliedAt: string;
  status: UiStatus;
  credentialCheck: "passed" | "failed" | "pending";
  permissionCheck: "passed" | "failed" | "pending";
  terminalStatus: "pending" | "creating" | "online" | "offline" | "none";
  note?: string | null;
  submissionRevision?: number | null;
  reviewRevision?: number | null;
}

const statusChip: Record<UiStatus, { cls: string; label: string; icon: string }> = {
  unknown: {cls:"bg-surface text-text-muted",label:"待确认",icon:"ri-question-line"},
  pending: { cls: "bg-amber-500/10 text-amber-500", label: "待人工审核", icon: "ri-time-fill" },
  provisioning: { cls: "bg-brand/10 text-brand", label: "EA 开通中", icon: "ri-loader-4-line" },
  connected: { cls: "bg-emerald-500/10 text-emerald-500", label: "审核通过", icon: "ri-checkbox-circle-fill" },
  rejected: { cls: "bg-red-500/10 text-red-500", label: "已拒绝", icon: "ri-close-circle-fill" },
  need_more: { cls: "bg-amber-500/10 text-amber-500", label: "要求补充", icon: "ri-file-add-line" },
};

function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString("zh-CN", { hour12: false });
}

function toRow(item: FollowerDisplay): SubAccountRow {
  const statusMap: Record<string, UiStatus> = {
    reviewing: "pending",
    ea_provisioning: "provisioning",
    connected: "connected",
    rejected: "rejected",
    need_material: "need_more",
  };
  return {
    facts: item,
    id: item.review_id,
    followerAccountId: item.follower_account_id,
    userRef: item.user_ref,
    broker: item.broker,
    server: item.server,
    accountNumber: item.mt4_login_masked,
    appliedAt: formatDateTime(item.submitted_at),
    status: item.review.status === "approved" ? "connected" : statusMap[item.account_status] || "unknown",
    credentialCheck: "pending",
    permissionCheck: "pending",
    terminalStatus: item.terminal_status === "creating" ? "creating" : item.terminal_status === "online" ? "online" : item.terminal_status === "offline" ? "offline" : item.terminal_status === "none" ? "none" : "pending",
    note: item.reason,
    submissionRevision: item.submission_revision,
    reviewRevision: item.review_revision,
  };
}

function Chip({ chip }: { chip: { cls: string; label: string; icon: string } }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${chip.cls}`}>
      <i className={`${chip.icon} text-[9px]`} />{chip.label}
    </span>
  );
}

export default function SubAccountsPage() {
  const [page,setPage]=useState(1),[total,setTotal]=useState(0),[counts,setCounts]=useState<Record<string,number>>({}),[revision,setRevision]=useState(0);
  const [rows, setRows] = useState<SubAccountRow[]>([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionStates, setActionStates] = useState<Record<string, ActionType>>({});
  const operationKeys = useRef<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const reviewFilter=({pending:'pending',connected:'approved',rejected:'rejected',need_more:'need_material'} as Record<string,string>)[statusFilter];
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    let pending=false;
    const load=()=>{if(pending||document.hidden)return;pending=true;return displayApi.followers(page,reviewFilter,search)
      .then((result) => {
        if (!cancelled) {setRows(result.items.map(toRow));setTotal(result.total);setCounts(result.stats||{});setError("");}
      })
      .catch(() => {
        if (!cancelled) setError("子账号审核队列加载失败，请检查后端服务或登录态。");
      })
      .finally(() => {
        pending=false;if (!cancelled) setLoading(false);
      });
    };void load();const timer=setInterval(()=>void load(),30000);
    return () => { cancelled = true;clearInterval(timer); };
  }, [page,reviewFilter,search,revision]);

  const filtered = rows;

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  const handleAction = async (id: string, action: ActionType) => {
    const row = rows.find((item) => item.id === id);
    if (!row?.submissionRevision || !row.reviewRevision) {
      showToast("error", "审核资料版本缺失，请刷新后重试。");
      return;
    }
    const slot = `${id}:${action}:${row.submissionRevision}:${row.reviewRevision}`;
    const operationKey = operationKeys.current[slot] ||= crypto.randomUUID();
    setActionStates((prev) => ({ ...prev, [id]: action }));
    try {
      await forexApi.patchFollowerAccountReview(id, {
        decision: action === "need_more" ? "request_material" : action,
        reason: action === "reject" ? "审核拒绝" : action === "need_more" ? "要求补充交易账号授权材料" : "人工审核通过",
        expected_submission_revision: row.submissionRevision,
        expected_review_revision: row.reviewRevision,
      }, operationKey);
      setRevision(value=>value+1);
      invalidateOperatorSummary();
      delete operationKeys.current[slot];
      showToast("success", action === "approve" ? "审核通过，等待自动开通 EA" : action === "reject" ? "已拒绝该申请" : "已要求用户补充资料");
    } catch {
      showToast("error", "审核操作失败，请确认后端服务和登录态。");
    } finally {
      setActionStates((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const stats = [
    {label:'待人工审核',value:counts.pending,color:'text-amber-600'},
    {label:'待补充',value:counts.need_material,color:'text-amber-600'},
    {label:'审核通过',value:counts.approved,color:'text-brand'},
    {label:'已拒绝',value:counts.rejected,color:'text-red-600'},
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">子账号接入</h2>
          <p className="text-xs text-text-muted mt-0.5">子账号提交后由管理员审核；审核通过后自动创建 EA 开通任务。</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
            <input
              type="text"
              placeholder="搜索用户/账号..."
              maxLength={100}
              value={search}
              onChange={(e) => {setSearch(e.target.value);setPage(1);}}
              className="h-8 w-[220px] pl-9 pr-3 rounded-lg bg-surface text-xs text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none"
            />
          </div>
          <select value={statusFilter} onChange={(e) => {setStatusFilter(e.target.value);setPage(1);}} className="h-8 px-2.5 rounded-lg bg-surface text-xs text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
            <option value="">全部</option>
            <option value="pending">待人工审核</option>

            <option value="connected">审核通过</option>
            <option value="rejected">已拒绝</option>
            <option value="need_more">要求补充</option>
          </select>
          <span className="text-xs text-text-muted">{loading ? "加载中..." : `${total} 条`}</span>
        </div>
      </div>
      <p className="text-xs text-text-muted">当前列表：{statusFilter==='pending'?'待人工审核':statusFilter==='connected'?'审核通过':statusFilter==='rejected'?'已拒绝':statusFilter==='need_more'?'要求补充':'全部审核状态'}{search?` · 搜索“${search}”`:''}。列表数量随筛选变化；下方四项为服务端提供的审核状态统计。</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {stats.map((item) => (
          <div key={item.label} className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
            <p className="text-[10px] text-text-muted">{item.label}</p>
            <p className={`text-lg font-semibold font-mono ${item.color}`}>{loading||error||item.value===undefined?"—":item.value}</p>
          </div>
        ))}
      </div>

      {error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1180px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">申请ID</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">用户</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">经纪商/服务器</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">交易账号</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">开通状态</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">最近上报</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">EA 连接</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">审核状态</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px] sticky right-0 bg-surface">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const acting = actionStates[row.id];
                return (
                  <tr key={row.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                    <td className="px-4 py-2.5 text-text-primary font-mono text-[11px] whitespace-nowrap">{row.id}</td>
                    <td className="px-4 py-2.5">
                      <p className="text-text-primary text-xs font-medium whitespace-nowrap">{row.userRef}</p>
                      <p className="text-text-muted text-[10px] font-mono">{row.followerAccountId}</p>
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary text-[11px] whitespace-nowrap">{row.broker} · {row.server}</td>
                    <td className="px-4 py-2.5 text-text-primary font-mono text-[11px] whitespace-nowrap">{row.accountNumber}<div className="mt-1"><TradingPlatformBadge platform={row.facts.platform}/></div></td>
                    <td className="px-4 py-2.5 text-center">{({approved:'已开通',failed:'开通失败',canceled:'已取消',unknown:'暂无开通记录',pending:'等待开通',running:'开通中'} as Record<string,string>)[row.facts.provisioning.status] || '状态待确认'}{row.facts.provisioning.status==='failed'&&<small className="block text-red-600">请查看详情中的失败证据</small>}</td>
                    <td className="px-4 py-2.5 text-center">{row.facts.connection.last_seen_at?formatDateTime(row.facts.connection.last_seen_at):'暂无上报'}</td>
                    <td className="px-4 py-2.5 text-center"><ConnectionCell facts={row.facts}/></td>
                    <td className="px-4 py-2.5 text-center"><Chip chip={statusChip[row.status]} /></td>
                    <td className="px-4 py-2.5 text-center sticky right-0 bg-white">
                      <div className="flex items-center justify-center gap-1">
                        <Link to={`/forex/sub-accounts/detail?id=${row.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap">
                          <i className="ri-eye-line text-[11px]" />详情
                        </Link>
                        {row.status === "pending" && (
                          <>
                            <button onClick={() => handleAction(row.id, "approve")} disabled={!!acting||!!error||loading} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-60 whitespace-nowrap">{acting === "approve" ? "处理中" : "审核通过"}</button>
                            <button onClick={() => handleAction(row.id, "reject")} disabled={!!acting||!!error||loading} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-60 whitespace-nowrap">{acting === "reject" ? "处理中" : "拒绝"}</button>
                            <button onClick={() => handleAction(row.id, "need_more")} disabled={!!acting||!!error||loading} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-60 whitespace-nowrap">{acting === "need_more" ? "处理中" : "补充"}</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-text-muted text-sm" colSpan={9}>{statusFilter==='pending'&&!search?'当前没有待人工审核的申请，可切换“全部”查看其他子账号':search?'没有符合搜索条件的申请':'当前筛选条件下没有申请记录'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-between text-sm"><span>第 {page} 页</span><div className="flex gap-4"><button disabled={loading||page===1} onClick={()=>setPage(v=>v-1)}>上一页</button><button disabled={loading||page*20>=total} onClick={()=>setPage(v=>v+1)}>下一页</button><button onClick={()=>setRevision(v=>v+1)}>刷新</button></div></div>
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} text-base`} />{toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
